import React, { useEffect, useRef, useState, useCallback } from 'react';
import {Link, useLocation} from 'react-router-dom';
import LivePulse from "../components/LivePulse";
import {PRIMARY_COLOR} from "../contants/Colors";

const API_BASE = process.env.REACT_APP_API_LOCAL_UPLOAD_URL + '/api';
const EMOJI_MAP = { angry:'😠', sad:'😢', neutral:'😐', happy:'😊', excited:'🤩' };
const COLORS    = { angry:'#ef4444', sad:'#f97316', neutral:'#94a3b8', happy:'#22c55e', excited:'#8b5cf6' };

function useReveal(threshold = 0.15) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, visible];
}

function Counter({ target, suffix = '', duration = 1800 }) {
    const [count, setCount] = useState(0);
    const [ref, visible] = useReveal();
    useEffect(() => {
        if (!visible) return;
        let start = 0;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(Math.floor(start));
        }, 16);
        return () => clearInterval(timer);
    }, [visible, target, duration]);
    return <span style={{color: PRIMARY_COLOR}} ref={ref}>{count.toLocaleString()}<span style={{color: PRIMARY_COLOR}}>{suffix}</span></span>;
}

function Reveal({ children, delay = 0, direction = 'up' }) {
    const [ref, visible] = useReveal();
    const transforms = { up: 'translateY(32px)', left: 'translateX(-32px)', right: 'translateX(32px)' };
    return (
        <div ref={ref} style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : (transforms[direction] || 'translateY(32px)'),
            transition: `opacity .65s ease ${delay}ms, transform .65s ease ${delay}ms`,
        }}>
            {children}
        </div>
    );
}

function StatBlock({ number, suffix, label, delay }) {
    return (
        <Reveal delay={delay}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(36px,5vw,52px)', lineHeight: 1, color: '#fff', marginBottom: 8 }}>
                    <Counter target={number} suffix={suffix} />
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '.1em' }}>{label}</div>
            </div>
        </Reveal>
    );
}

function FeatureCard({ icon, title, desc, delay }) {
    return (
        <Reveal delay={delay}>
            <div style={{
                background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 20, padding: '28px 24px', height: '100%',
                transition: 'border-color .25s, background .25s, transform .25s',
            }}
                 onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(255,46,88,.35)'; e.currentTarget.style.background = 'rgba(255,46,88,.04)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                 onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.08)'; e.currentTarget.style.background = 'rgba(255,255,255,.035)'; e.currentTarget.style.transform = 'none'; }}
            >
                <div style={{ fontSize: 28, marginBottom: 14 }}>{icon}</div>
                <h3 style={{ fontFamily: 'Outfit-SemiBold, sans-serif', fontSize: 16, color: '#fff', marginBottom: 9, margin: '0 0 9px' }}>{title}</h3>
                <p style={{ fontSize: 13, lineHeight: 1.75, color: 'rgba(255,255,255,0.45)', margin: 0 }}>{desc}</p>
            </div>
        </Reveal>
    );
}

function HowColumn({ role, emoji, color, steps, delay }) {
    return (
        <Reveal delay={delay}>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 20, padding: '36px 28px' }}>
                <div style={{ fontSize: 32, marginBottom: 6 }}>{emoji}</div>
                <div style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 20, color, marginBottom: 24 }}>{role}</div>
                {steps.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, background: `${color}18`, border: `1px solid ${color}40`, color, fontSize: 10, fontFamily: 'Outfit-SemiBold, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{s}</span>
                    </div>
                ))}
            </div>
        </Reveal>
    );
}

function LiveStatCard({ delay, floatDuration, children }) {
    return (
        <div style={{
            animation: `float ${floatDuration}s ease-in-out ${delay}s infinite`,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 18,
            padding: '20px 22px',
            backdropFilter: 'blur(12px)',
            minWidth: 180,
            flex: '1 1 180px'
        }}>
            {children}
        </div>
    );
}

function SentimentBar({ data }) {
    return (
        <>
            <div style={{display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', gap: 1, marginTop: 8}}>
                {data.map((d, i) => (
                    <div key={i} style={{flex: d.v, background: d.c, minWidth: 2}} title={d.l}/>
                ))}
            </div>
            <div style={{display: 'flex', overflow: 'hidden', gap: 1, marginTop: 8}}>
                {data.map((d, i) => (
                    <div key={'n' + i} style={{
                        minWidth: 2, fontSize: 10, color: d.c, fontWeight: 500,
                        display: 'flex', justifyContent: 'center', marginRight: 5
                    }} title={d.l}>
                        <span key={i} style={{fontSize: 12, color: d.c, fontWeight: 500}}>{EMOJI_MAP[d.l.toLowerCase()]} {d.v}</span>
                        {/*{d.v}*/}
                    </div>
                ))}
            </div>
        </>
    );
}

function PricingCard({plan, url, symbol, price, perLocation, desc, features, highlight, color, delay}) {
    return (
        <Reveal delay={delay}>
            <div style={{
                background: highlight ? 'rgba(255,46,88,0.07)' : 'rgba(255,255,255,0.03)',
                border: highlight ? '1.5px solid rgba(255,46,88,0.5)' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: 22,
                padding: '36px 30px',
                position: 'relative',
                transition: 'transform .25s',
                height: '100%',
            }}
                 onMouseOver={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                 onMouseOut={e => e.currentTarget.style.transform = 'none'}
            >
                {highlight && (
                    <div style={{
                        position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                        background: 'linear-gradient(135deg, rgb(255,46,88), rgb(180,20,60))',
                        color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 16px',
                        borderRadius: 20, letterSpacing: '.06em', textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                    }}>Most popular</div>
                )}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, color: color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 10 }}>{plan}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>{symbol}</span>
                        <span style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 44, color: '#fff', lineHeight: 1 }}>{price}</span>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>/mo</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 12 }}>{perLocation}</div>
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 }}>{desc}</p>
                </div>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 20, marginBottom: 24 }}>
                    {features.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 11 }}>
                            <div style={{ width: 18, height: 18, borderRadius: '50%', background: `${color}20`, border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                <svg width="9" height="9" viewBox="0 0 9 9"><polyline points="1.5,4.5 3.5,6.5 7.5,2.5" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.58)', lineHeight: 1.5 }}>{f}</span>
                        </div>
                    ))}
                </div>
                <Link to={url} style={{ textDecoration: 'none', display: 'block' }}>
                    <button style={{
                        width: '100%', padding: '13px', borderRadius: 11, border: highlight ? 'none' : `1px solid ${color}40`,
                        background: highlight ? 'linear-gradient(135deg, rgb(255,46,88), rgb(180,20,60))' : `${color}15`,
                        color: highlight ? '#fff' : color,
                        fontSize: 14, fontFamily: 'Outfit-SemiBold, sans-serif', cursor: 'pointer',
                        transition: 'all .2s',
                    }}
                            onMouseOver={e => { e.currentTarget.style.opacity = '.85'; }}
                            onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                        Get 30 Days Free Trial Now
                    </button>
                </Link>
            </div>
        </Reveal>
    );
}

// ── Business Directory Card ───────────────────────────────────────────────
function BusinessCard({ biz }) {
    const { businessName, businessType, tagline, logoUrl, primaryColor, isTopPerformer, locations, stats } = biz;
    const API_IMG = process.env.REACT_APP_API_LOCAL_UPLOAD_URL?.replace('/api','') || '';
    const today = stats?.today || {};
    const weekly = stats?.weekly?.sentimentCounts || {};
    const allTime = stats?.allTime || {};

    const weeklyTotal = Object.values(weekly).reduce((a, b) => a + b, 0);
    const weeklyData = Object.entries(weekly)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ v, c: COLORS[k], l: k }));

    const scoreColor = allTime.avgScore >= 4 ? '#22c55e' : allTime.avgScore >= 3 ? '#f59e0b' : '#ef4444';

    return (
        <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${isTopPerformer ? 'rgba(255,46,88,.45)' : 'rgba(255,255,255,0.09)'}`,
            borderRadius: 18,
            padding: '22px 20px',
            position: 'relative',
            transition: 'transform .25s, border-color .25s',
            cursor: 'default',
        }}
             onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = 'rgba(255,46,88,.35)'; }}
             onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = isTopPerformer ? 'rgba(255,46,88,.45)' : 'rgba(255,255,255,0.09)'; }}
        >
            {isTopPerformer && (
                <div style={{ position:'absolute', top:-11, right:14, background:'linear-gradient(135deg,rgb(255,46,88),rgb(180,20,60))', color:'#fff', fontSize:10, fontWeight:600, padding:'3px 10px', borderRadius:20, letterSpacing:'.06em' }}>
                    ⭐ Top Performer
                </div>
            )}

            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                {logoUrl ? (
                    <img src={`${API_IMG}${logoUrl}`} alt={businessName}
                         style={{ width:44, height:44, borderRadius:10, objectFit:'contain', background:`${primaryColor}18`, padding:4, border:`1px solid ${primaryColor}30`, flexShrink:0 }} />
                ) : (
                    <div style={{ width:44, height:44, borderRadius:10, background:`#FF2E5818`, border:`1px solid #FF2E5830`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>🏢</div>
                )}
                <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:15, color:'#fff', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{businessName}</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', marginTop:1 }}>
                        {businessType && <span style={{ color: '#fff', fontSize:10, fontWeight:500, marginRight:6 }}>{businessType}</span>}
                        {locations?.[0]?.city || biz.city || ''}
                    </div>
                </div>
                {allTime.avgScore && (
                    <div style={{ textAlign:'center', flexShrink:0 }}>
                        <div style={{ fontWeight:700, fontSize:18, color:scoreColor, lineHeight:1 }}>{allTime.avgScore}</div>
                        <div style={{ fontSize:9, color:'rgba(255,255,255,.3)', textTransform:'uppercase' }}>avg</div>
                    </div>
                )}
            </div>

            {/* Today's mood */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20, background:'rgba(255,255,255,.03)', borderRadius:10, padding:'9px 12px' }}>
                <span style={{ fontSize:22 }}>{today.dominantEmoji || '😐'}</span>
                <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:600, color: today.dominantSentiment ? COLORS[today.dominantSentiment] : '#94a3b8', textTransform:'capitalize' }}>
                        {today.dominantSentiment || 'No data'} today
                    </div>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,.3)', marginTop:1 }}>
                        {today.total > 0 ? `${today.total} feedback${today.total === 1 ? '' : 's'} today` : 'No feedback today yet'}
                    </div>
                </div>
            </div>

            {/* Weekly sentiment bar */}
            {weeklyTotal > 0 && (
                <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,.3)', marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>This week · {weeklyTotal} responses</div>
                    <div style={{ display:'flex', height:5, borderRadius:3, overflow:'hidden', gap:1 }}>
                        {weeklyData.map((d, i) => (
                            <div key={i} title={`${d.l}: ${d.v}`} style={{ flex:d.v, background:d.c, minWidth:2 }} />
                        ))}
                    </div>
                    <div style={{ display:'flex', gap:6, marginTop:5, flexWrap:'wrap' }}>
                        {weeklyData.map((d, i) => (
                            <span key={i} style={{ fontSize:14, color:d.c, fontWeight:500 }}>{EMOJI_MAP[d.l]} {d.v}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Locations */}
            {locations?.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:8 }}>
                    {locations.slice(0,3).map((loc, i) => (
                        <span key={i} style={{ fontSize:10, background:'rgba(255,255,255,.06)', color:'rgba(255,255,255,.45)', borderRadius:6, padding:'4px 8px 2px 8px' }}>
                            📍 {loc.name}{loc.city ? `, ${loc.city}` : ''}
                        </span>
                    ))}
                    {locations.length > 3 && (
                        <span style={{ fontSize:10, color:'rgba(255,255,255,.3)' }}>+{locations.length - 3} more</span>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Directory Section ─────────────────────────────────────────────────────
function DirectorySection() {
    const [businesses, setBusinesses] = useState([]);
    const [businessTypes, setBusinessTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [geoLocating, setGeoLocating] = useState(false);
    const [userCity, setUserCity] = useState('');

    const fetchBusinesses = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            if (params.search)       q.set('search', params.search);
            if (params.businessType) q.set('businessType', params.businessType);
            if (params.lat)          q.set('lat', params.lat);
            if (params.lng)          q.set('lng', params.lng);
            q.set('limit', '24');
            const res = await fetch(`${API_BASE}/directory/businesses?${q}`);
            const data = await res.json();
            setBusinesses(data.success ? data.data : []);
            // Show which city was detected from geolocation
            if (data.detectedCity) setUserCity(data.detectedCity);
        } catch { setBusinesses([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        // Fetch business types for filter
        fetch(`${API_BASE}/directory/business-types`)
            .then(r => r.json())
            .then(d => { if (d.success) setBusinessTypes(d.data); })
            .catch(() => {});

        // Try geolocation on mount
        if (navigator.geolocation) {
            setGeoLocating(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setGeoLocating(false);
                    fetchBusinesses({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                },
                () => {
                    setGeoLocating(false);
                    fetchBusinesses({});
                },
                { timeout: 5000 }
            );
        } else {
            fetchBusinesses({});
        }
    }, [fetchBusinesses]);

    const handleSearch = (e) => {
        e.preventDefault();
        setUserCity('');
        fetchBusinesses({ search, businessType: selectedType });
    };

    const handleTypeFilter = (type) => {
        setSelectedType(type);
        fetchBusinesses({ search, businessType: type });
    };

    const handleLocationSearch = (e) => {
        e.preventDefault();
        if (!locationSearch.trim()) return;
        setSearch(locationSearch);
        setUserCity('');
        fetchBusinesses({ search: locationSearch, businessType: selectedType });
    };

    const handleGeolocate = () => {
        setGeoLocating(true);
        setUserCity('');
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setGeoLocating(false);
                fetchBusinesses({ lat: pos.coords.latitude, lng: pos.coords.longitude, businessType: selectedType });
            },
            () => { setGeoLocating(false); },
            { timeout: 8000 }
        );
    };

    return (
        <section id="businesses" className="section-pad" style={{ padding:'100px 40px', borderTop:'1px solid rgba(255,255,255,.06)' }}>
            <div style={{ maxWidth:1100, margin:'0 auto' }}>
                <Reveal>
                    <div style={{ textAlign:'center', marginBottom:52 }}>
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: 14, width: '100%', justifyContent: 'center'}}>
                            <LivePulse label={''} color={PRIMARY_COLOR} marginTop={-1}/>
                            <div style={{
                                fontSize: 11,
                                color: 'rgb(255,46,88)',
                                fontWeight: 500,
                                letterSpacing: '.12em',
                                textTransform: 'uppercase',
                                marginLeft: 10
                            }}>Live on Percept Pulse
                            </div>
                        </div>
                        <h2 style={{
                            fontFamily: 'Outfit-Black, sans-serif',
                            fontSize: 'clamp(28px,4vw,48px)',
                            letterSpacing: '-1.5px',
                            color: '#fff',
                            lineHeight: 1.1,
                            marginBottom: 14
                        }}>
                            Discover businesses<br />
                            <span style={{ color:'rgba(255,255,255,.22)' }}>near you, right now.</span>
                        </h2>
                        <p style={{ fontSize:15, color:'rgba(255,255,255,.4)', maxWidth:480, margin:'0 auto' }}>
                            See real-time mood, today's feedback count, and weekly sentiment from businesses using Percept Pulse.
                        </p>
                    </div>
                </Reveal>

                {/* Search bar */}
                <Reveal delay={100}>
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:20, justifyContent:'center' }}>
                        <form onSubmit={handleLocationSearch} style={{ display:'flex', gap:8, flex:'1 1 300px', maxWidth:420 }}>
                            <input
                                value={locationSearch}
                                onChange={e => setLocationSearch(e.target.value)}
                                placeholder="Search by city or business name..."
                                style={{ flex:1, padding:'11px 16px', borderRadius:10, border:'1px solid rgba(255,255,255,.12)', background:'rgba(255,255,255,.06)', color:'#fff', fontSize:13, outline:'none', fontFamily:'inherit' }}
                            />
                            <button type="submit" style={{ padding:'11px 18px', borderRadius:10, border:'none', background:'rgb(255,46,88)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>Search</button>
                        </form>
                        <button
                            onClick={handleGeolocate}
                            disabled={geoLocating}
                            style={{ padding:'11px 18px', borderRadius:10, border:'1px solid rgba(255,255,255,.15)', background:'rgba(255,255,255,.06)', color:'rgba(255,255,255,.7)', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}
                        >
                            {geoLocating ? '📡 Locating…' : '📍 Near me'}
                        </button>
                        <button
                            onClick={() => { setSearch(''); setSelectedType(''); setLocationSearch(''); setUserCity(''); fetchBusinesses({}); }}
                            style={{ padding:'11px 14px', borderRadius:10, border:'1px solid rgba(255,255,255,.1)', background:'transparent', color:'rgba(255,255,255,.4)', fontSize:13, cursor:'pointer' }}
                        >
                            All
                        </button>
                    </div>

                    {/* Business type filters */}
                    {businessTypes.length > 0 && (
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'center', marginBottom:userCity ? 20 : 60 }}>
                            {businessTypes.map(type => (
                                <button
                                    key={type}
                                    onClick={() => handleTypeFilter(selectedType === type ? '' : type)}
                                    style={{
                                        padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer',
                                        border:`1px solid ${selectedType === type ? 'rgb(255,46,88)' : 'rgba(255,255,255,.12)'}`,
                                        background: selectedType === type ? 'rgba(255,46,88,.15)' : 'rgba(255,255,255,.04)',
                                        color: selectedType === type ? 'rgb(255,100,130)' : 'rgba(255,255,255,.5)',
                                        transition:'all .2s',
                                    }}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    )}
                </Reveal>

                {/* Detected city label */}
                {userCity && !loading && (
                    <div style={{ textAlign:'center', marginBottom:30, fontSize:13, color:'rgba(255,255,255,.4)' }}>
                        📍 Showing businesses in <strong style={{ color:'rgb(255,100,130)' }}>{userCity}</strong>
                        <button
                            onClick={() => { setUserCity(''); setSearch(''); setSelectedType(''); setLocationSearch(''); fetchBusinesses({}); }}
                            style={{ marginLeft:10, background:'none', border:'none', color:'rgba(255,255,255,.3)', fontSize:12, cursor:'pointer', textDecoration:'underline' }}
                        >Show all cities</button>
                    </div>
                )}

                {/* Results */}
                {loading ? (
                    <div style={{ textAlign:'center', padding:'60px 0' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:10, color:'rgba(255,255,255,.4)', fontSize:14 }}>
                            <svg width="18" height="18" viewBox="0 0 18 18" style={{ animation:'spin 1s linear infinite' }}>
                                <circle cx="9" cy="9" r="7" stroke="rgba(255,46,88,.4)" strokeWidth="2" fill="none"/>
                                <path d="M9 2 A7 7 0 0 1 16 9" stroke="rgb(255,46,88)" strokeWidth="2" fill="none" strokeLinecap="round"/>
                            </svg>
                            Loading businesses…
                        </div>
                    </div>
                ) : businesses.length === 0 ? (
                    <div style={{ textAlign:'center', padding:'60px 0', color:'rgba(255,255,255,.35)' }}>
                        <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
                        <div style={{ fontSize:15 }}>No businesses found.</div>
                        <div style={{ fontSize:13, marginTop:6 }}>Try searching a different city or removing the filter.</div>
                    </div>
                ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
                        {businesses.map(biz => (
                            <BusinessCard key={biz.tenantId} biz={biz} />
                        ))}
                    </div>
                )}
            </div>
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </section>
    );
}

// 1. Define your custom manual pricing tiers per country code
const PRICING_TIERS = {
    US: { symbol: "$", starter: "20", growth: "18", enterprise: "15" },
    PK: { symbol: "PKR ", starter: "5,000", growth: "4,500", enterprise: "3,750" }
};

const LOCATION_TIERS = {
    starter: '1 Location', growth: '2–5 locations', enterprise: '6–15 locations'
}

function PricingSection() {
    // Default to US pricing if location detection fails
    const [activeTier, setActiveTier] = useState(PRICING_TIERS.US);

    useEffect(() => {
        fetch('https://api.ipinfo.io/lite/me?token=8fa28a56c70719')
            .then((res) => {
                if (!res.ok) throw new Error('IPInfo request failed');
                return res.json();
            })
            .then((data) => {
                // ipinfo uses 'country_code' in its Lite JSON payload
                if (data && data.country_code === 'PK') {
                    setActiveTier(PRICING_TIERS.PK);
                }
            })
            .catch((err) => {
                console.warn("Location check failed. Defaulting to standard USD pricing.", err);
                setActiveTier(PRICING_TIERS.US);
            });
    }, []);

    return (
        <>
            <PricingCard delay={0}   plan="Starter" url={`/contact?locations=${LOCATION_TIERS.starter}`}    symbol={activeTier.symbol} price={activeTier.starter} perLocation="per location / month" color="rgba(148,163,184,1)" desc="Perfect for a single branch. Everything you need to start collecting feedback." features={['1 location included','Unlimited staff','Emoji + audio feedback','AI transcription & translation','Shareable links (SMS & email)','Basic analytics dashboard','Custom branding','Customer database']} />
            <PricingCard delay={100} plan="Growth" url={`/contact?locations=${LOCATION_TIERS.growth}`}      symbol={activeTier.symbol} price={activeTier.growth} perLocation="per location / month" color="rgb(255,46,88)" highlight desc="For businesses with multiple branches. Volume discount applied automatically." features={['2–5 locations','Everything in Starter','Location performance tab','Staff leaderboards','Date range analytics','Priority support','Volume discount (~10% off)','Custom feedback forms per location']} />
            <PricingCard delay={200} plan="Enterprise" url={`/contact?locations=${LOCATION_TIERS.enterprise}`}  symbol={activeTier.symbol} price={activeTier.enterprise} perLocation="per location / month" color="rgb(139,92,246)" desc="For large operations with 6+ branches. Maximum discount, dedicated support." features={['6-15 locations','Everything in Growth','Dedicated account manager','Custom integrations','Webhook support','White-label option','Volume discount (~25% off)','SLA guarantee']} />
        </>
    );
}

// ── MAIN ──────────────────────────────────────────────────────────────────
export default function Website() {
    const [scrollY, setScrollY] = useState(0);

    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            // Find the element with the ID matching the hash (minus the '#' symbol)
            const targetElement = document.getElementById(hash.replace('#', ''));

            if (targetElement) {
                // Give the DOM a tiny split-second to finish rendering before scrolling
                setTimeout(() => {
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    }, [hash]); // Fires whenever the hash changes

    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);

    const SENTIMENT_DATA = [
        { v: 3, c: '#ef4444', l: 'Angry' },
        { v: 5, c: '#f97316', l: 'Sad' },
        { v: 8, c: '#94a3b8', l: 'Neutral' },
        { v: 24, c: '#22c55e', l: 'Happy' },
        { v: 12, c: '#8b5cf6', l: 'Excited' },
    ];

    return (
        <div style={{ background: '#080512', minHeight: '100vh', overflowX: 'hidden', color: '#fff' }}>
            <style>{`
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes gradShift{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:none}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .hero-grad{background:linear-gradient(135deg,rgb(255,46,88),rgb(190,18,55),rgb(120,55,195),rgb(59,28,98));background-size:300% 300%;animation:gradShift 5s ease infinite;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .hero-line-spacer{height: 15px;}
        .glow-btn{transition:all .25s!important;}
        .glow-btn:hover{box-shadow:0 0 36px rgba(255,46,88,.45)!important;transform:translateY(-2px)!important;}
        .nav-link{color:rgba(255,255,255,.5);text-decoration:none;font-size:14px;transition:color .2s;}
        .nav-link:hover{color:#fff;}
        .nav-links { display: flex; }
        .mobile-signin { display: none; }
        @media(max-width:768px){
          .nav-links { display: none !important; }
          .mobile-signin { display: block !important; }
          .hero-line-spacer{height: 8px}
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 28px 20px !important; }
          .features-grid { grid-template-columns: 1fr !important; }
          .how-grid { grid-template-columns: 1fr !important; }
          .ai-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .pricing-cols { grid-template-columns: 1fr !important; gap: 24px !important; }
          .live-stats-row { flex-direction: column !important; }
          .footer-inner { flex-direction: column !important; text-align: center !important; gap: 12px !important; }
          .hero-ctas { flex-direction: column !important; align-items: center !important; }
          .emoji-strip { gap: 8px !important; }
          .emoji-item { min-width: auto !important; }
          .section-pad { padding: 72px 20px !important; }
          .cta-pad { padding: 52px 24px !important; }
          .nav-inner { padding: 0 20px !important; }
          .hero-h1 { font-size: 42px !important; letter-spacing: -1.5px !important; }
        }
        @media(max-width:480px){
          .hero-h1 { font-size: 34px !important; }
          .emoji-strip { gap: 6px !important; }
        }
      `}</style>

            {/* ── NAV ── */}
            <nav style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: 80,
                background: scrollY > 60 ? 'rgba(8,5,18,0.92)' : 'transparent',
                backdropFilter: scrollY > 60 ? 'blur(20px)' : 'none',
                borderBottom: scrollY > 60 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                transition: 'all .3s',
            }}>
                <div className="nav-inner" style={{ maxWidth: 1100, margin: '0 auto', padding: '0px 40px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <img src={'https://pub-267dd4744ace49cf8306de8e7c530c15.r2.dev/assets/Logo-Color.png'} style={{width: 100}} alt="logo"/>
                    </div>
                    <div className="nav-links" style={{ gap: 36, alignItems: 'center' }}>
                        <a href="#features" className="nav-link">Features</a>
                        <a href="#how" className="nav-link">How it works</a>
                        {/*<a href="/businesses" className="nav-link">Businesses</a>*/}
                        <a href="#pricing" className="nav-link">Pricing</a>
                        <Link to="https://app.theperceptpulse.com/" target={'_blank'} style={{ textDecoration: 'none' }}>
                            <button style={{ background: 'rgba(255,46,88,.1)', border: '1px solid rgba(255,46,88,.3)', color: 'rgb(255,100,130)', padding: '7px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all .2s' }}
                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,46,88,.2)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(255,46,88,.1)'}>
                                Sign in
                            </button>
                        </Link>
                    </div>
                    <div className="mobile-signin">
                        <Link to="https://app.theperceptpulse.com/" target={'_blank'} style={{ textDecoration: 'none' }}>
                            <button style={{ background: 'rgba(255,46,88,.1)', border: '1px solid rgba(255,46,88,.3)', color: 'rgb(255,100,130)', padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Sign in</button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* ── HERO ── */}
            <section className="section-pad" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '130px 40px 90px', textAlign: 'center' }}>
                <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                    <div style={{ position: 'absolute', top: '8%', left: '10%', width: 640, height: 640, borderRadius: '50%', background: 'radial-gradient(circle,rgba(255,46,88,.1),transparent 65%)', filter: 'blur(50px)', animation: 'float 9s ease-in-out infinite' }} />
                    <div style={{ position: 'absolute', bottom: '5%', right: '8%', width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle,rgba(59,28,98,.22),transparent 65%)', filter: 'blur(50px)', animation: 'float 11s ease-in-out 1s infinite reverse' }} />
                    <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.02) 1px,transparent 1px)', backgroundSize: '72px 72px' }} />
                </div>
                <div style={{ maxWidth: 860, position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,46,88,.1)', border: '1px solid rgba(255,46,88,.22)', borderRadius: 100, padding: '5px 16px', marginBottom: 32, animation: 'fadeIn .8s ease both' }}>
                        <LivePulse label={''} color={PRIMARY_COLOR} marginTop={-1}/>
                        <span style={{ fontSize: 11, color: 'rgba(255,160,175,.9)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.08em' }}>AI-Powered Customer Feedback</span>
                    </div>
                    <h1 className="hero-h1" style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(40px,6.5vw,80px)', lineHeight: 1.03, letterSpacing: '-2.5px', marginBottom: 26, animation: 'fadeUp .9s ease .1s both' }}>
                        Your customers<br />
                        <span className="hero-grad">speak.</span><br />
                        <div className={'hero-line-spacer'}/>
                        <div>Hear what they say.</div>
                    </h1>
                    <p style={{ fontSize: 'clamp(15px,1.8vw,19px)', lineHeight: 1.75, color: 'rgba(255,255,255,.45)', maxWidth: 540, margin: '0 auto 46px', animation: 'fadeUp .9s ease .25s both' }}>
                        Real-time customer sentiment through emoji surveys and voice feedback - across every branch, every session, every language.
                    </p>
                    <div className="hero-ctas" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 64, animation: 'fadeUp .9s ease .4s both' }}>
                        <Link to="/contact" style={{ textDecoration: 'none' }}>
                            <button className="glow-btn" style={{ background: 'linear-gradient(135deg,rgb(255,46,88),rgb(185,15,52))', border: 'none', color: '#fff', padding: '15px 38px', borderRadius: 12, fontSize: 15, fontFamily: 'Outfit-SemiBold, sans-serif', cursor: 'pointer', letterSpacing: '.02em' }}>Contact Us</button>
                        </Link>
                        <a href="#how" style={{ textDecoration: 'none' }}>
                            <button style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', color: 'rgba(255,255,255,.65)', padding: '15px 34px', borderRadius: 12, fontSize: 15, fontFamily: 'Outfit-SemiBold, sans-serif', cursor: 'pointer', transition: 'background .2s' }}
                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,.09)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,.05)'}>
                                See how it works
                            </button>
                        </a>
                    </div>
                    <div className="emoji-strip" style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', animation: 'fadeUp .9s ease .55s both' }}>
                        {[
                            { e: '😠', c: '#ef4444', l: 'Angry',   d: 5.0 },
                            { e: '😢', c: '#f97316', l: 'Sad',     d: 5.5 },
                            { e: '😐', c: '#94a3b8', l: 'Neutral', d: 6.0 },
                            { e: '😊', c: '#22c55e', l: 'Happy',   d: 6.5 },
                            { e: '🤩', c: '#8b5cf6', l: 'Excited', d: 7.0 },
                        ].map((x, i) => (
                            <div key={i} className="emoji-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, animation: `float ${x.d}s ease-in-out ${i * 0.18}s infinite` }}>
                                <div style={{ width: 52, height: 52, borderRadius: 13, background: `${x.c}16`, border: `1px solid ${x.c}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, boxShadow: `0 4px 20px ${x.c}18` }}>{x.e}</div>
                                <span style={{ fontSize: 10, color: x.c, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.07em' }}>{x.l}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── GLOBAL STATS ── */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', borderBottom: '1px solid rgba(255,255,255,.06)', padding: '72px 40px' }}>
                <div className="stats-grid" style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 40 }}>
                    <StatBlock number={50} suffix="+"  label="Languages supported"     delay={0} />
                    <StatBlock number={3}  suffix="s"  label="Average feedback time"   delay={100} />
                    <StatBlock number={99} suffix="%"  label="Uptime guaranteed"       delay={200} />
                    <StatBlock number={10} suffix="x"  label="More responses vs paper" delay={300} />
                </div>
            </div>

            {/* ── LIVE STATS SECTION ── */}
            <section className="section-pad" style={{ padding: '100px 40px' }}>
                <div style={{ maxWidth: 1060, margin: '0 auto' }}>
                    <Reveal>
                        <div style={{ textAlign: 'center', marginBottom: 60 }}>
                            <div style={{ fontSize: 11, color: 'rgb(255,46,88)', fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 14 }}>Real-time insights</div>
                            <h2 style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(28px,4vw,48px)', letterSpacing: '-1.5px', color: '#fff', lineHeight: 1.1 }}>
                                Your dashboard,<br />
                                <span style={{ color: 'rgba(255,255,255,.25)' }}>always live.</span>
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal delay={100}>
                        <div className="live-stats-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <LiveStatCard delay={0} floatDuration={6}>
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>Today's mood</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                    <span style={{ fontSize: 34 }}>😊</span>
                                    <div>
                                        <div style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 22, color: '#22c55e', lineHeight: 1 }}>Happy</div>
                                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 2 }}>4.2 / 5 avg score</div>
                                    </div>
                                </div>
                                <SentimentBar data={SENTIMENT_DATA} />
                                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.07)', fontSize: 11, color: 'rgba(255,255,255,.3)' }}>52 responses today</div>
                            </LiveStatCard>

                            <LiveStatCard delay={0.3} floatDuration={7}>
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>Top location</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                    <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,46,88,.15)', border: '1px solid rgba(255,46,88,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>📍</div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: '#fff' }}>Marina Branch</div>
                                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>Dubai</div>
                                    </div>
                                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                        <div style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 22, color: '#22c55e', lineHeight: 1 }}>4.7</div>
                                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)' }}>avg score</div>
                                    </div>
                                </div>
                                {[
                                    { l: 'Marina', v: 85, c: 'rgb(255,46,88)' },
                                    { l: 'Downtown',  v: 62, c: 'rgba(255,46,88,.55)' },
                                    { l: 'Jumeirah',      v: 44, c: 'rgba(255,46,88,.3)' },
                                ].map((b, i) => (
                                    <div key={i} style={{ marginBottom: 7 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>{b.l}</span>
                                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>{b.v}</span>
                                        </div>
                                        <div style={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,.06)' }}>
                                            <div style={{ height: '100%', borderRadius: 3, background: b.c, width: `${b.v}%`, transition: 'width 1s ease' }} />
                                        </div>
                                    </div>
                                ))}
                            </LiveStatCard>

                            <LiveStatCard delay={0.6} floatDuration={6.5}>
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>Top staff</div>
                                {[
                                    { n: 'Salman',   s: '4.8', r: 47, medal: '🥇' },
                                    { n: 'Alizeh', s: '4.2', r: 31, medal: '🥈' },
                                    { n: 'Danish',   s: '3.9', r: 18, medal: '🥉' },
                                ].map((st, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 2 ? '1px solid rgba(255,255,255,.06)' : 'none' }}>
                                        <span style={{ fontSize: 16 }}>{st.medal}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{st.n}</div>
                                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)' }}>{st.r} responses</div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: parseFloat(st.s) >= 4.5 ? '#22c55e' : parseFloat(st.s) >= 4 ? '#f59e0b' : '#94a3b8' }}>{st.s}</div>
                                            <div style={{ fontSize: 9, color: 'rgba(255,255,255,.25)' }}>score</div>
                                        </div>
                                    </div>
                                ))}
                            </LiveStatCard>

                            <LiveStatCard delay={0.9} floatDuration={7.5}>
                                <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>7-day trend</div>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 100, marginBottom: 8 }}>
                                            {[12, 18, 9, 24, 31, 22, 38].map((v, i) => (
                                                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '200%' }}>
                                                    <div style={{ width: '100%', borderRadius: '3px 3px 0 0', background: i === 6 ? 'rgb(255,46,88)' : 'rgba(255,46,88,.3)', height: `${((v / 38) * 50)}%`, transition: 'height 1s ease' }}/>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                                                <span key={i} style={{ fontSize: 10, color: i === 6 ? 'rgb(255,100,130)' : 'rgba(255,255,255,.25)', flex: 1, textAlign: 'center' }}>{d}</span>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{fontSize: 11, color: 'rgba(255,255,255,.3)'}}>This week</span>
                                            <span style={{fontSize: 13, fontWeight: 600, color: '#22c55e'}}>+28% ↑</span>
                                        </div>
                                    </div>
                                </div>
                            </LiveStatCard>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── FEATURES ── */}
            <section id="features" className="section-pad" style={{padding: '100px 40px', borderTop: '1px solid rgba(255,255,255,.06)'}}>
                <div style={{maxWidth: 1060, margin: '0 auto'}}>
                    <Reveal>
                        <div style={{textAlign: 'center', marginBottom: 64}}>
                            <div style={{ fontSize: 11, color: 'rgb(255,46,88)', fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 14 }}>Everything you need</div>
                            <h2 style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(28px,4vw,48px)', letterSpacing: '-1.5px', color: '#fff', lineHeight: 1.1 }}>
                                Built for the real world.<br/>
                                <span style={{color: 'rgba(255,255,255,.22)'}}>Not the boardroom.</span>
                            </h2>
                        </div>
                    </Reveal>
                    <div className="features-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14}}>
                        <FeatureCard delay={0}   icon="😊" title="Emoji feedback"     desc="Five-point emoji scale - angry to excited. Customers respond in under 3 seconds. No typing, no friction."/>
                        <FeatureCard delay={80}  icon="🎙️" title="Voice feedback"     desc="Speak in any language. Our system transcribes, translates to English, and extracts sentiment automatically."/>
                        <FeatureCard delay={160} icon="📍" title="Location intelligence" desc="Every piece of feedback tagged to a branch. See which location is thriving and which needs attention."/>
                        <FeatureCard delay={0} icon={<span style={{display:'flex', gap:6, alignItems:'center'}}>
                            🔗
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" style={{color:'#fff', opacity:.9}}>
                                <path d="M3 3h7v7H3V3zm2 2v3h3V5H5zm7-2h7v7h-7V3zm2 2v3h3V5h-3zM3 13h7v7H3v-7zm2 2v3h3v-3H5zm9-2h2v2h-2v-2zm2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm2 2h2v2h-2v-2zm2-6h2v2h-2v-2zm0 4h2v2h-2v-2z"/>
                            </svg>
                        </span>} title="Shareable Links & QR Codes" desc="Send personalised links via SMS or email, or generate a QR code. Stick it at your exit, counter, or table. Scan, tap, done - no app, no friction, forever open." />
                        <FeatureCard delay={80}  icon="🌐" title="50+ languages"      desc="Urdu, Arabic, French - Our system detects, translates, and analyzes sentiment in any language."/>
                        <FeatureCard delay={160} icon="🏆" title="Staff performance"  desc="Ranked leaderboards. See who collects the most feedback and earns the happiest responses." />
                        <FeatureCard delay={0}   icon="🎨" title="Full branding"      desc="Upload your logo, set brand colours. Every customer-facing page reflects your identity." />
                        <FeatureCard delay={80}  icon="👥" title="Customer database"  desc="Optional contact capture. Build a list for offers and re-engagement automatically." />
                        <FeatureCard delay={160} icon="📊" title="Live dashboard"     desc="Today's pulse, 7-day trends, sentiment breakdowns, top performers - one glanceable home screen." />
                    </div>
                </div>
            </section>

            {/* ── HOW IT WORKS ── */}
            <section id="how" className="section-pad" style={{ padding: '100px 40px', background: 'rgba(255,255,255,.016)', borderTop: '1px solid rgba(255,255,255,.06)', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                    <Reveal>
                        <div style={{ textAlign: 'center', marginBottom: 64 }}>
                            <div style={{ fontSize: 11, color: 'rgb(255,46,88)', fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 14 }}>Simple by design</div>
                            <h2 style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(28px,4vw,48px)', letterSpacing: '-1.5px', color: '#fff', lineHeight: 1.1 }}>
                                Three roles.<br />
                                <span style={{ color: 'rgba(255,255,255,.22)' }}>One seamless loop.</span>
                            </h2>
                        </div>
                    </Reveal>
                    <div className="how-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
                        <HowColumn delay={0}   role="Tenant"   emoji="🏢" color="#8b5cf6" steps={['Register your business', 'Add branches & staff', 'Create emoji feedback forms', 'Generate shareable links', 'View real-time analytics']} />
                        <HowColumn delay={120} role="Staff"    emoji="👤" color="#8b5cf6" steps={['Log in to staff portal', 'Enter customer bill ID', 'Hand device to customer', 'Track session history', 'See your performance rank']} />
                        <HowColumn delay={240} role="Customer" emoji="🙋" color="#8b5cf6" steps={['Opens link or sees device', 'Chooses emoji or voice', 'Rates in under 5 seconds', 'Optionally shares contact', 'Gets offers & rewards']} />
                    </div>
                </div>
            </section>

            {/* ── AI SECTION ── */}
            <section className="section-pad" style={{ padding: '100px 40px' }}>
                <div className="ai-grid" style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
                    <Reveal direction="left">
                        <div>
                            <div style={{ fontSize: 11, color: 'rgb(255,46,88)', fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 14 }}>AI-powered</div>
                            <h2 style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(26px,3.5vw,42px)', letterSpacing: '-1px', color: '#fff', lineHeight: 1.15, marginBottom: 20 }}>
                                Speaks every language.<br />
                                <span style={{ color: 'rgba(255,255,255,.25)' }}>Understands every feeling.</span>
                            </h2>
                            {[
                                { icon: '🎙️', text: 'Customer records voice in any language' },
                                { icon: '🌐', text: 'System detects language & transcribes' },
                                { icon: '🇬🇧', text: 'Automatically translated to English' },
                                { icon: '😊', text: 'System classifies sentiment: angry → excited' },
                            ].map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(255,46,88,.1)', border: '1px solid rgba(255,46,88,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>{item.icon}</div>
                                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}>{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal direction="right" delay={150}>
                        <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 18, padding: 26 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                                <LivePulse label={''} color={'#22c55e'} marginTop={0}/>
                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.08em' }}>Analysis complete</span>
                                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,.2)' }}>0.8s</span>
                            </div>
                            <div style={{ display: 'flex', gap: 7, marginBottom: 14, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 5, background: '#dbeafe', color: '#1e40af',
                                    fontWeight: 500
                                }}>🇫🇷 Français detected</span>
                                <span style={{
                                    fontSize: 11,
                                    padding: '3px 10px',
                                    borderRadius: 5,
                                    background: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.32)' }}>Translated →</span>
                                <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 5, background: '#dbeafe', color: '#1d4ed8', fontWeight: 500 }}>English</span>
                            </div>
                            <div style={{
                                background: 'rgba(255,255,255,.03)',
                                borderRadius: 9,
                                padding: '11px 13px',
                                marginBottom: 9
                            }}>
                                <div style={{
                                    fontSize: 10,
                                    color: 'rgba(255,255,255,.25)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '.06em',
                                    marginBottom: 5
                                }}>Original (French)
                                </div>
                                <p style={{fontSize: 13, color: 'rgba(255,255,255,.6)', lineHeight: 1.6, margin: 0}}>"Le
                                    service était excellent et la nourriture était délicieuse."</p>
                            </div>
                            <div style={{
                                background: 'rgba(59,130,246,.08)',
                                border: '1px solid rgba(59,130,246,.15)',
                                borderRadius: 9,
                                padding: '11px 13px', marginBottom: 14 }}>
                                <div style={{ fontSize: 10, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>English translation</div>
                                <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)',
                                    lineHeight: 1.6,
                                    margin: 0
                                }}>"The service was excellent and the food was delicious."</p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.28)' }}>Sentiment:</span>
                                <span style={{ fontSize: 20 }}>🤩</span>
                                <span style={{ fontSize: 12, fontWeight: 500, color: '#8b5cf6', padding: '3px 10px', borderRadius: 7, background: 'rgba(139,92,246,.12)' }}>Excited</span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── DIRECTORY ── */}
            {/*<DirectorySection />*/}

            {/* ── PWA STRIP ── */}
            <section className="section-pad" style={{ padding: '80px 40px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                <div style={{ maxWidth: 1060, margin: '0 auto' }}>
                    <Reveal>
                        <div style={{ textAlign: 'center', marginBottom: 48 }}>
                            <div style={{ fontSize: 11, color: 'rgb(255,46,88)', fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 14 }}>Always with you</div>
                            <h2 style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(24px,3.5vw,42px)', letterSpacing: '-1.5px', color: '#fff', lineHeight: 1.1 }}>
                                Miss nothing.<br />
                                <span style={{ color: 'rgba(255,255,255,.22)' }}>Be everywhere.</span>
                            </h2>
                        </div>
                    </Reveal>
                    <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
                        {[
                            { icon: '📱', title: 'Install as an App', desc: 'Add to your home screen on Android, iPhone, or desktop. No App Store. No download. Tap the icon and it opens instantly like a native app.', tag: 'Android · iOS · Desktop', tagColor: '#22c55e', delay: 0 },
                            { icon: '🔔', title: 'Push Notifications', desc: 'Get alerted the moment a customer leaves angry or sad feedback - even when the dashboard is closed. Real-time alerts, straight to your device.', tag: 'Instant · Even when closed', tagColor: '#FF2E58', delay: 80 },
                            { icon: '📴', title: 'Works Offline', desc: 'Pages load instantly from cache even without internet. Staff portals stay accessible on slow connections. No dropped sessions mid-feedback.', tag: 'Cached · Always fast', tagColor: '#8b5cf6', delay: 160 },
                        ].map((item, i) => (
                            <Reveal key={i} delay={item.delay}>
                                <div style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '28px 24px', height: '100%', transition: 'border-color .25s, background .25s, transform .25s' }}
                                     onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(255,46,88,.35)'; e.currentTarget.style.background = 'rgba(255,46,88,.04)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                                     onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.08)'; e.currentTarget.style.background = 'rgba(255,255,255,.035)'; e.currentTarget.style.transform = 'none'; }}
                                >
                                    <div style={{ fontSize: 28, marginBottom: 14 }}>{item.icon}</div>
                                    <h3 style={{ fontFamily: 'Outfit-SemiBold, sans-serif', fontSize: 16, color: '#fff', margin: '0 0 9px' }}>{item.title}</h3>
                                    <p style={{ fontSize: 13, lineHeight: 1.75, color: 'rgba(255,255,255,0.45)', margin: '0 0 16px' }}>{item.desc}</p>
                                    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: `${item.tagColor}18`, border: `1px solid ${item.tagColor}30`, color: item.tagColor, letterSpacing: '.04em' }}>{item.tag}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PRICING ── */}
            <section id="pricing" className="section-pad" style={{ padding: '100px 40px', background: 'rgba(255,255,255,.016)', borderTop: '1px solid rgba(255,255,255,.06)', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <div style={{ maxWidth: 1060, margin: '0 auto' }}>
                    <Reveal>
                        <div style={{ textAlign: 'center', marginBottom: 64 }}>
                            <div style={{ fontSize: 11, color: 'rgb(255,46,88)', fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 14 }}>Simple pricing</div>
                            <h2 style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(28px,4vw,48px)', letterSpacing: '-1.5px', color: '#fff', lineHeight: 1.1, marginBottom: 14 }}>
                                Pay per location.<br />
                                <span style={{ color: 'rgba(255,255,255,.22)' }}>Scale as you grow.</span>
                            </h2>
                            <p style={{ fontSize: 15, color: 'rgba(255,255,255,.38)', maxWidth: 480, margin: '0 auto' }}>Every plan includes unlimited staff, unlimited feedback, and full AI audio analysis. Add more locations anytime.</p>
                        </div>
                    </Reveal>
                    <div className="pricing-cols" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, alignItems: 'start' }}>
                        <PricingSection/>
                    </div>
                    <Reveal delay={200}>
                        <div style={{ textAlign: 'center', marginTop: 36 }}>
                            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.3)', marginBottom: 4 }}>All plans include a 30-day free trial. No credit card required.</p>
                            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.3)' }}>Need a custom quote for 15+ locations?
                                <Link style={{textDecoration: 'none'}} to={`/contact?locations=15%2B%20locations`}><span style={{color: 'rgb(255,100,130)', cursor: 'pointer'}}>{" "}Talk to us</span></Link></p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="section-pad" style={{ padding: '90px 40px' }}>
                <Reveal>
                    <div className="cta-pad" style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center', background: 'linear-gradient(135deg,rgba(255,46,88,.1),rgba(59,28,98,.2))', border: '1px solid rgba(255,46,88,.18)', borderRadius: 26, padding: '72px 48px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 480, height: 220, background: 'radial-gradient(ellipse,rgba(255,46,88,.1),transparent 70%)', pointerEvents: 'none' }} />
                        <h2 style={{ fontFamily: 'Outfit-Black, sans-serif', fontSize: 'clamp(26px,4vw,46px)', letterSpacing: '-1.5px', color: '#fff', lineHeight: 1.1, marginBottom: 16, position: 'relative' }}>
                            Ready to hear what your<br />customers really think?
                        </h2>
                        <p style={{ fontSize: 16, color: 'rgba(255,255,255,.38)', marginBottom: 40, position: 'relative' }}>Set up in under 5 minutes. No credit card required.</p>
                        <div className="hero-ctas" style={{ display: 'flex', gap: 12, justifyContent: 'center', position: 'relative', flexWrap: 'wrap' }}>
                            <Link to="/contact" style={{ textDecoration: 'none' }}>
                                <button className="glow-btn" style={{ background: 'linear-gradient(135deg,rgb(255,46,88),rgb(175,12,48))', border: 'none', color: '#fff', padding: '15px 40px', borderRadius: 11, fontSize: 15, fontFamily: 'Outfit-SemiBold, sans-serif', cursor: 'pointer', letterSpacing: '.02em' }}>
                                    Contact Us Now
                                </button>
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* ── FOOTER ── */}
            <footer style={{ padding: '32px 40px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                <div className="footer-inner" style={{ maxWidth: 1060, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 9}}>
                        <img src={'https://pub-267dd4744ace49cf8306de8e7c530c15.r2.dev/assets/Logo-Color.png'} style={{width: 100}} alt="logo"/>
                    </div>
                    <p style={{fontSize: 12, color: 'rgba(255,255,255,.2)'}}>© {new Date().getFullYear()} Percept Pulse. All rights reserved.</p>
                    <div style={{ display: 'flex', gap: 22 }}>
                        <Link to="https://app.theperceptpulse.com/tenant/login" target={"_blank"} style={{ fontSize: 12, color: 'rgba(255,255,255,.28)', textDecoration: 'none', transition: 'color .2s' }} onMouseOver={e => e.target.style.color = 'rgba(255,255,255,.6)'} onMouseOut={e => e.target.style.color = 'rgba(255,255,255,.28)'}>Tenant login</Link>
                        <Link to="https://app.theperceptpulse.com/feedback-taker/login" target={"_blank"} style={{ fontSize: 12, color: 'rgba(255,255,255,.28)', textDecoration: 'none', transition: 'color .2s' }} onMouseOver={e => e.target.style.color = 'rgba(255,255,255,.6)'} onMouseOut={e => e.target.style.color = 'rgba(255,255,255,.28)'}>Staff login</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}